import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { INK } from "../chartTheme";
import BackgroundLetterAvatarsLogo from "../../components/AvatarLogo";
import CustomizedProgressBars from "../../components/Progressbar";
import "./style.css";

const UNASSIGNED = "Unassigned";

/**
 * Who is carrying what in the selected sprint.
 * Rows are ordered by load: most assigned issues first, then by how much of
 * that load is still open, so the heaviest / least-finished person reads first.
 * "Unassigned" is a bucket, not a person, so it always sits at the bottom.
 */
export default function TeamWorkload({ pieChartData = [] }) {
  const rows = useMemo(() => {
    const assignees = pieChartData?.[0]?.AssigneGroupObj || [];

    return assignees
      .map((each) => {
        const { employeeDetails, assignedIssues = [] } = each;
        const name = employeeDetails?.employeeName || employeeDetails?.employeeEmail || UNASSIGNED;
        const total = assignedIssues.length;
        const done = assignedIssues.filter((issue) => issue?.status === "Done").length;

        return {
          name,
          total,
          done,
          open: total - done,
          progress: total === 0 ? 0 : Math.ceil((done * 100) / total),
          isUnassigned: employeeDetails?.employeeName === UNASSIGNED,
        };
      })
      .sort((a, b) => {
        if (a.isUnassigned !== b.isUnassigned) return a.isUnassigned ? 1 : -1;
        if (b.total !== a.total) return b.total - a.total;      // biggest load first
        if (b.open !== a.open) return b.open - a.open;          // then most left to do
        return a.name.localeCompare(b.name);
      });
  }, [pieChartData]);

  const members = rows.filter((row) => !row.isUnassigned).length;
  const totalIssues = rows.reduce((sum, row) => sum + row.total, 0);
  const heaviest = rows.find((row) => !row.isUnassigned && row.total > 0);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Typography sx={{ fontSize: "12px !important", color: INK.muted, mb: 1.25 }}>
        {members} {members === 1 ? "member" : "members"} · {totalIssues} {totalIssues === 1 ? "task" : "tasks"}
      </Typography>

      {rows.length === 0 ? (
        <Box className="workload-outer-Box" sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
          <Typography sx={{ fontSize: "13px !important", color: INK.muted }}>
            No tasks assigned in this sprint
          </Typography>
        </Box>
      ) : (
        <Box className="workload-outer-Box">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              pt: 1,
              pb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px !important", fontWeight: 650, color: INK.muted, letterSpacing: "0.04em", flex: "1 1 auto" }}>
              MEMBER
            </Typography>
            <Typography sx={{ fontSize: "11px !important", fontWeight: 650, color: INK.muted, letterSpacing: "0.04em", width: 130, flex: "0 0 auto" }}>
              PROGRESS
            </Typography>
            <Typography sx={{ fontSize: "11px !important", fontWeight: 650, color: INK.muted, letterSpacing: "0.04em", width: 64, flex: "0 0 auto", textAlign: "right" }}>
              TASKS
            </Typography>
          </Box>

          <Box className="workload-list-Box">
            {rows.map((row, index) => (
              <Box
                key={`${row.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.75,
                  borderBottom: `1px solid ${INK.grid}`,
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <BackgroundLetterAvatarsLogo LogoTitle={row.isUnassigned ? "" : row.name} />

                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "13px !important",
                        color: INK.primary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.name}
                    </Typography>
                    {heaviest && row === heaviest && rows.length > 1 ? (
                      <Typography
                        sx={{
                          fontSize: "10px !important",
                          fontWeight: 700,
                          color: "#b3541e",
                          backgroundColor: "#fdf1e7",
                          borderRadius: "999px",
                          px: 0.75,
                          py: "1px",
                          flex: "0 0 auto",
                          letterSpacing: "0.03em",
                        }}
                      >
                        HIGHEST LOAD
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography sx={{ fontSize: "11.5px !important", color: INK.muted }}>
                    {row.done} done · {row.open} open
                  </Typography>
                </Box>

                <Box sx={{ width: 130, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: "1 1 auto" }}>
                    <CustomizedProgressBars value={row.progress} />
                  </Box>
                  <Typography sx={{ fontSize: "12px !important", color: INK.secondary, width: 32, flex: "0 0 auto", textAlign: "right" }}>
                    {row.progress}%
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "12px !important", color: INK.primary, fontWeight: 650, width: 64, flex: "0 0 auto", textAlign: "right" }}>
                  {row.total} {row.total === 1 ? "task" : "tasks"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
